// Server-mode auth client. Talks only to the daemon's /api/auth/* proxy
// (apps/daemon/src/auth-routes.ts) — never to Gini Code's simo-auth service
// directly, so no third-party origin or secret ever reaches the browser.
// On local/desktop installs (no OD_AUTH_MODE=server) `/api/auth/me` always
// reports `{ authenticated: true, mode: 'local' }`, so every function here
// is a no-op in practice for the existing desktop/dev flow.

export interface AuthMeResponse {
  authenticated: boolean;
  mode: 'local' | 'server' | 'native';
  userId?: string;
  tenantId?: string;
  email?: string;
  plan?: string;
}

export interface AuthActionError {
  ok: false;
  error: string;
}

export interface AuthActionSuccess {
  ok: true;
  user?: unknown;
  limits?: unknown;
}

export type AuthActionResult = AuthActionSuccess | AuthActionError;

export async function fetchAuthStatus(): Promise<AuthMeResponse> {
  try {
    const resp = await fetch('/api/auth/me', { cache: 'no-store' });
    const body = (await resp.json().catch(() => ({}))) as Partial<AuthMeResponse>;
    if (resp.ok) {
      return { authenticated: true, mode: body.mode ?? 'local', ...body };
    }
    return { authenticated: false, mode: body.mode ?? 'server' };
  } catch {
    // Can't reach the daemon at all — treat as local/unauthenticated-not-required
    // rather than locking the user out of an app that may just be booting.
    return { authenticated: true, mode: 'local' };
  }
}

async function postAuthAction(path: string, body: unknown): Promise<AuthActionResult> {
  try {
    const resp = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = (await resp.json().catch(() => ({}))) as Record<string, unknown>;
    if (!resp.ok) {
      return { ok: false, error: typeof json.error === 'string' ? json.error : 'Something went wrong' };
    }
    return { ok: true, user: json.user, limits: json.limits };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

export function loginWithPassword(email: string, password: string): Promise<AuthActionResult> {
  return postAuthAction('/api/auth/login', { email, password });
}

export function registerWithInvite(
  email: string,
  password: string,
  name?: string,
): Promise<AuthActionResult> {
  return postAuthAction('/api/auth/register', { email, password, name });
}

// Second leg of the Gini Code handoff — see AuthCallback.tsx.
export function exchangeOAuthCode(code: string, state: string): Promise<AuthActionResult> {
  return postAuthAction('/api/auth/oauth/exchange', { code, state });
}

// First leg of the Studio-initiated hosted flow (as opposed to the
// Gini-Code-initiated handoff exchangeOAuthCode above): a full browser
// navigation, not a fetch — the daemon's GET /api/auth/oauth/start mints
// state/PKCE and 302s to the Gini authorization endpoint, so the token
// exchange never happens in a context the page's JS can observe.
export function beginHostedLogin(): void {
  window.location.assign('/api/auth/oauth/start');
}

export async function logoutSession(): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } catch {
    // Best-effort — a failed logout call just leaves a stale cookie that
    // expires on its own (7 days) rather than blocking the user.
  }
}
